/*
** drawing variable -- maintaining and attaching webgl Attribute and Uniform variables
** Copyright (C) 2021-2026 Tactile Interactive, all rights reserved
*/

let traceGLCalls = false;
let traceUniforms = false;
let traceAttributes = false;  // quick update every reload
let traceAttributesFull = false;  // full dumps every reload

// attr arrays and uniforms that can change on every frame.
// you can set a static value or a function that'll return it
// The set is drawing-specific.

// abstract superclass for all of these.
export class drawingVariable {
	// the drawing is what it's used in.  Must have a:
	// gl, drawVariables array, program,
	// reloadFunc will AUTOMATICALLY be called before each frame to refresh the value
	constructor(varName, drawing, reloadFunc) {
		this.drawing = drawing;
		this.gl = drawing.gl;
		this.tagObject = drawing.tagObject;

		this.reloadFunc = reloadFunc;

		this.varName = varName;
		if (! varName || ! drawing)
			throw new Error(`bad name ${varName} or drawing ${drawing} `
				+` used to create a view var`);

		// whichever variable this is a subclass of, constructors will need this
		// or maybe this is already done and so this is superflous?
		// maybe this isn't needed 4:46 drawing.setDrawing();

		if (traceGLCalls) dblog(`🍯 created drawingVariable '${varName}', drawing:`, drawing);
	}

	addVariable(drawing) {
		let vv = drawing.drawVariables;
		if (vv.includes(this)) {
			console.error(`🍯 duplicate variable ${varName}!!`);
			return null;
		}
		vv.push(this);
	}

}


/* ************************************************** uniforms for variables */

function isFiniteAr(value) {
	if (typeof value == 'object') {
		// they ALL must be finite
		for (let v of value) {
			if (!isFiniteAr(v))
				return false;
		}
		return true;
	}
	else
		return isFinite(value);
}

// uniforms don't vary from one vertex to another
// create this as many times as you have uniforms as input to either or both shaders
export class drawingUniform extends drawingVariable {
	constructor(varName, drawing, reloadFunc) {
		super(varName, drawing, reloadFunc);

		 this.addVariable(drawing);
		// this turns out to be an internal magic object

		this.uniformLoc = this.gl.getUniformLocation(this.drawing.program, varName);
		if (!this.uniformLoc)
			throw new Error(`Cannot find uniform loc for uniform variable ${varName} `
				+` in ${this.drawing.program.$qLabel}`);
		if (traceUniforms) {
			dblog(`🍯 created drawingUniform '${varName}'  in `
						+` ${this.drawing.program.$qLabel}`);
		}

		this.reloadVariable();
	}

	// set the uniform to it - upload latest value to GPU
	// call this when the uniform's value changes, to reload it into the GPU
	// actually, calls before each draw, always
	reloadVariable() {
		if (traceUniforms)
			dblog(`🍯🍯 re-loading uniform '${this.varName}' ...`);

		// value might be a JS float, typed vector or matrix, typically 4 or 4x4.
		// type is typically Matrix4fv, 1fv, or others; see
		// https://developer.mozilla.org - WebGLRenderingContext
		const {value, type} =  this.reloadFunc();
		if (traceUniforms)
			dblog(`	  ...with value=${value} type ${type}`);

		// you can't pass null or undefined to webgl, but remember this
		this.value = value;
		if (null == value || !isFiniteAr(value) || null == type) {
			// this means, we won't draw this particular drawing this time around.
			// better luck next time.
			let msg = `uniform variable ${this.varName} has `
						+`a problematic value(${value}) or no type(${type})`;
			console.error(`🍯 ${msg}`);
			this.drawing.skipDrawingCuzErr = msg;
			return;
		}

		const gl = this.gl;
		const method = `uniform${type}`;

		// like gl.uniformMatrix4f(this.uniformLoc, value) or uniform1f() etc
		let args = [this.uniformLoc];
		if (/^Matrix/.test(type))
			args.push(false);  // do not transpose

		// if array is a Normal JS array (not typed), string the components along in the arg list
		if (Array.isArray(value)) {
			args = args.concat(value);  // typed arrays return false, not Arrays
		}
		else {
			args.push(value);
		}

		gl[method].apply(gl, args);

		if (traceUniforms) {
			dblog(`🍯 drawingUniform reloaded U variable '${this.varName}' in `);
			dblog(`			   ${this.drawing.avatarLabel} uniform gl.${method}()`, args);
			dblog(`            value = `, value);
		}
	}
}


/* *********************************************** attributes for arrays */
// attributes DO change from one vertex to another; each row of the attr array
// is given to the vertex shader, which crunches whatever and decides upon the
// coordinates and color for that point

// create this as many times as you have buffers as input to vec shader
// always float32. reloadFunc() must return a Float32TypedArray with a non-array property
// 'nTuples' that gives the number of rows or vertices used, each tupleWidth number of floats
// eg for x & y, tupleWidth=2. sequence of several tuples gets draw, however.
export class drawingAttribute extends drawingVariable {
	constructor(varName, drawing, tupleWidth, reloadFunc = () => {}) {
		super(varName, drawing, reloadFunc);
		const gl = this.gl;
		this.tupleWidth = tupleWidth;  // num of float32s in each row/tuple
		this.sceneName = this.drawing.sceneName;
		// maybe this isn't needed 4:46 this.drawing.setDrawing();

		this.addVariable(drawing);

		// small integer indicating which attr this is.
		// Set by compileProgram() for each attr in each drawing in each shader in GL context
		this.attrLocation = this.gl.getAttribLocation(drawing.program, varName);

		if (this.attrLocation < 0) {
			throw new Error(`🍯 drawingAttribute:attr loc for '${varName}' is bad: `+
				this.attrLocation);
		}

		// create gl GPU buffer
		this.glBuffer = gl.createBuffer();

		// various ways to label it; each works with a difft debugging system. some obsolete. 😟
		let label = `${drawing.avatarLabel}-${drawing.drawingName}-${varName}-glbuf`;
		this.tagObject(this.glBuffer, label);
		this.glBuffer.$qLabel = label;
		this.glBuffer.__SPECTOR_Metadata = {name: label};

		// we haven't touched the actual JS data yet, this does it.  Although a lot of
		// other stuff isn't ready.
		this.reloadVariable();
	}

	// call this when the array's values change, to reload them into the GPU.
	// reloadFunc() gets past the previous array (or undefined first time)
	// must return another Float32Array (or same) with extra JS property 'nTuples'
	reloadVariable() {
		const gl = this.gl;
		// maybe this isn't needed 4:46 this.drawing.setDrawing();

		// get the latest from the real world.
		// WebGL2Fundamentals said we don't have to do this if it's the same buffer (w/diff values) every time.
		// if I understood them correctly. afraid to try... or maybe misunderstood...
		let floatArray = this.floatArray = this.reloadFunc();
		if (!floatArray) {
			throw `reloadFunc() returned null on var=${varName} scene=${this.drawing.drawingName}`;
		}
		this.nTuples = this.floatArray.nTuples;
		// ticDrawing can have zero tics when first created
		//if (!this.nTuples) throw `false nTuples ${this.nTuples} in ${this.varName} in ${this.sceneName}`;

		// connect	ARRAY_BUFFER to glBuffer.
		gl.bindBuffer(gl.ARRAY_BUFFER, this.glBuffer);
		gl.enableVertexAttribArray(this.attrLocation);

		gl.vertexAttribPointer(this.attrLocation, this.tupleWidth, gl.FLOAT,
				false, this.tupleWidth * 4, 0);	 // normalize, stride, offset
		if (traceGLCalls)
			dblog(`🍯  drawingAttribute '${this.varName}' connected to its `
				+` glBuffer to tupleWidth=${this.tupleWidth}`);

		gl.bufferData(gl.ARRAY_BUFFER, floatArray, gl.DYNAMIC_DRAW);
		if (traceAttributes || traceAttributesFull) {
			dblog(`🍯 reload drawingAttribute '${this.varName}' in ${this.drawing.drawingName}`
				+ ` reloaded, ${this.nTuples}  tuples of ${this.tupleWidth} floats each`);
		}

		if (traceAttributesFull) {
			for (let t = 0; t < this.nTuples; t++) {
				let line = `[${String(t).padStart(4)}]	`;
				for (let f = 0; f < this.tupleWidth; f++)
					line += `  ${this.floatArray[t * this.tupleWidth + f].toFixed(6)}`;
				dblog(line);
			}
		}
	}
}


// new math3d();
